if (!customElements.get('faq-accordion')) {
  class FaqAccordion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.abortController = new AbortController();
      this.addEventListener('click', this.handleClick.bind(this), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleClick(event) {
      const summary = event.target.closest('.faq-question');
      if (!summary) return;

      const details = summary.closest('.faq-item');
      const answer = details?.querySelector('[data-faq-answer]');
      if (!details || !answer) return;

      event.preventDefault();

      if (details.open) {
        this.collapse(details, answer);
        return;
      }

      if (this.dataset.singleOpen === 'true') {
        this.querySelectorAll('.faq-item.is-open').forEach((otherDetails) => {
          if (otherDetails === details) return;
          const otherAnswer = otherDetails.querySelector('[data-faq-answer]');
          if (otherAnswer) this.collapse(otherDetails, otherAnswer);
        });
      }

      this.expand(details, answer);

      if (this.dataset.swapImage === 'true') {
        this.updateActiveMedia(details.dataset.blockId);
      }
    }

    updateActiveMedia(blockId) {
      if (!blockId) return;

      const activeMedia = this.querySelector(`[data-faq-media="${CSS.escape(blockId)}"]`);
      if (!activeMedia) return;

      this.querySelectorAll('.faq-media__item.is-active').forEach((item) => item.classList.remove('is-active'));
      activeMedia.classList.add('is-active');
    }

    expand(details, answer) {
      details.classList.add('is-open');
      details.open = true;

      this.animateHeight(answer, 0, answer.scrollHeight, () => {
        answer.style.removeProperty('height');
        answer.style.removeProperty('overflow');
      });
    }

    collapse(details, answer) {
      details.classList.remove('is-open');

      this.animateHeight(answer, answer.scrollHeight, 0, () => {
        details.open = false;
        answer.style.removeProperty('height');
        answer.style.removeProperty('overflow');
      });
    }

    animateHeight(el, from, to, onDone) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        el.style.removeProperty('height');
        el.style.removeProperty('overflow');
        el.style.removeProperty('transition');
        delete el.dataset.faqAnimatingHeight;
        onDone();
        return;
      }

      // A click on another question can interrupt this element mid-transition
      // (still closing/opening). Clear the previous transitionend listener so
      // it doesn't also fire — and skip the from-px snap below when already
      // mid-animation, so the new transition retargets smoothly from the
      // element's current live height instead of jumping back to `from` first.
      if (el._faqHeightTransitionEnd) {
        el.removeEventListener('transitionend', el._faqHeightTransitionEnd);
        el._faqHeightTransitionEnd = null;
      }

      const isAnimating = el.dataset.faqAnimatingHeight === 'true';

      el.style.overflow = 'hidden';

      if (!isAnimating) {
        el.style.height = `${from}px`;
        el.getBoundingClientRect();
      }

      el.dataset.faqAnimatingHeight = 'true';
      el.style.transition = 'height var(--animation-default, .3s ease)';

      const handleTransitionEnd = (event) => {
        if (event.target !== el || event.propertyName !== 'height') return;
        el.removeEventListener('transitionend', handleTransitionEnd);
        el._faqHeightTransitionEnd = null;
        el.style.removeProperty('transition');
        delete el.dataset.faqAnimatingHeight;
        onDone();
      };

      el._faqHeightTransitionEnd = handleTransitionEnd;
      el.addEventListener('transitionend', handleTransitionEnd);
      requestAnimationFrame(() => {
        el.style.height = `${to}px`;
      });
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
}
